// Template.State — scaffolding + mirror-Template seed.
//
// Per `.docs/v1/design/build-order.md` §Slice 2 (authoring Templates)
// and §Slice 5 (mirror Templates, Q5). This file ships:
//
//   - The `TemplateName` brand.
//   - The `Template` record — a stub with just `ref` + `name`. The
//     full record (`shape` + `style` + `seedContent` + `targetFormats`
//     + `hardConstraints`) lands with the Template BC proper in
//     Slice 5.5+.
//   - `Templates` — the by-id map.
//   - Four *authoring* consts (`freeform`, `quiz`, `question`,
//     `pdfImport`) from Slice 2 with their `TemplateRef`s.
//   - Four *mirror* consts (`htmlQuiz`, `markdownQuiz`, `htmlQuestion`,
//     `markdownQuestion`) from Slice 5. Ship alongside authoring
//     Templates rather than in a separate registry — mirror Templates
//     ARE real Templates per `docs/model/bcs/packaging.md` §1, and
//     queries over "list all Templates" stay uniform. Each mirror
//     Template is referenced from the corresponding entry in the
//     Packaging registry via `mirrorTemplateRef` (per §6.4 of
//     `surface.md`).
//   - `initial` — a map pre-populated with all eight seeded Templates,
//     because they're always present (layer-defaults-are-runtime-
//     invariants). Reads never have to check for absence of a
//     built-in Template.
//
// No commands, no events, no errors, no evolve/decide at this slice
// (Slice 5.5+ lands the Template BC proper).
export { freeform, htmlQuestion, htmlQuiz, initial, markdownQuestion, markdownQuiz, mkTemplate, mkTemplateName, pdfImport, pqblTorus, question, quiz, };
import * as Version from "../versioning/version.js";
import * as IdMod from "./id.js";
import * as Ref from "./ref.js";
const mkTemplateName = (s) => s;
const mkTemplate = (ref, name) => ({
    ref,
    name,
});
// === Seeded consts ===
//
// The design refers to these by their design-side names `T_freeform`
// / `T_quiz` / `T_question` / `T_pdf_import`. Ids preserve the prefix
// so bundle serialisations line up with the design docs.
const freeform = mkTemplate(Ref.mk(IdMod.mk("T_freeform"), Version.v1), mkTemplateName("Freeform"));
const quiz = mkTemplate(Ref.mk(IdMod.mk("T_quiz"), Version.v1), mkTemplateName("Quiz"));
const question = mkTemplate(Ref.mk(IdMod.mk("T_question"), Version.v1), mkTemplateName("Question"));
const pdfImport = mkTemplate(Ref.mk(IdMod.mk("T_pdf_import"), Version.v1), mkTemplateName("PDF Import"));
// Composite template that pairs with F_torus to produce a real
// oli-torus zip with mapped MCQ activities. Content shape lives in
// `pqblTorus.ts` (Core.Template.PqblTorus.parse). Renderer dispatch
// on `(pqblTorus.ref, F_torus)` routes to the pqbl-torus renderer
// instead of the freeform text wrap.
const pqblTorus = mkTemplate(Ref.mk(IdMod.mk("T_pqbl_torus"), Version.v1), mkTemplateName("pQBL for Torus"));
// === Mirror Templates (Slice 5, Q5) ===
//
// Every T→F entry in the built-in Packaging registry names a mirror
// Template whose Shape mirrors the Format's structure. The four MVP
// T→F Packagings are `T_quiz → F_html`, `T_quiz → F_markdown`,
// `T_question → F_html`, `T_question → F_markdown`, so we ship one
// mirror Template per target Format for each authoring shape.
//
// Ids preserve the `t_*` prefix (lowercase to distinguish from the
// upper-case authoring ids); design-side names line up with
// bounded-contexts.md §2's table.
const htmlQuiz = mkTemplate(Ref.mk(IdMod.mk("t_html_quiz"), Version.v1), mkTemplateName("HTML Quiz"));
const markdownQuiz = mkTemplate(Ref.mk(IdMod.mk("t_markdown_quiz"), Version.v1), mkTemplateName("Markdown Quiz"));
const htmlQuestion = mkTemplate(Ref.mk(IdMod.mk("t_html_question"), Version.v1), mkTemplateName("HTML Question"));
const markdownQuestion = mkTemplate(Ref.mk(IdMod.mk("t_markdown_question"), Version.v1), mkTemplateName("Markdown Question"));
const initial = new Map([
    // Authoring Templates (Slice 2).
    [freeform.ref.id, freeform],
    [quiz.ref.id, quiz],
    [question.ref.id, question],
    [pdfImport.ref.id, pdfImport],
    [pqblTorus.ref.id, pqblTorus],
    // Mirror Templates (Slice 5).
    [htmlQuiz.ref.id, htmlQuiz],
    [markdownQuiz.ref.id, markdownQuiz],
    [htmlQuestion.ref.id, htmlQuestion],
    [markdownQuestion.ref.id, markdownQuestion],
]);
//# sourceMappingURL=state.js.map